import {
  Box,
  Button,
  Cascader,
  ErrorMessage,
  Flex,
  Text,
  Tooltip,
} from '@/components';
import { useWindowSize } from '@/hooks';
import { t } from '@/i18n';
import { NEST_ITEM_CONNECTOR } from '@/utils';
import {
  type CSSProperties,
  type ComponentProps,
  type ReactNode,
  cloneElement,
  isValidElement,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';

export type CascaderOption = {
  label: string;
  value: string | number;
  children?: CascaderOption[] | null;
  disabled?: boolean;
};

type CascaderValueType = Array<string | number>;

type Props<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  control: FormControlProp<T>;
  options?: CascaderOption[];
  errors: FieldErrors;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  loading?: boolean;
  enableIntermediate?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  buttonLabel?: string;
  width?: CSSProperties['width'];
  buttonOnClick?: () => void;
  wrapperStyle?: CSSProperties;
  hint?: string;
  shouldCoexistOnChange?: boolean;
  itemConnector?: string;
  useNotInOption?: boolean;
  textNotInOption?: string;
  labelSize?: FieldLabelSize;
  shouldDisplayLabelWithTooltip?: boolean;
  caption?: ReactNode;
} & (
  | {
      onChange?: (value: CascaderValueType | null) => void;
      multiple?: never;
      showFullPath?: never;
    }
  | {
      onChange?: (value: CascaderValueType[] | null) => void;
      multiple: true;
      showFullPath?: boolean;
    }
) &
  Pick<
    ComponentProps<typeof Cascader<CascaderOption>>,
    | 'fieldNames'
    | 'multiple'
    | 'displayRender'
    | 'dropdownRender'
    | 'showCheckedStrategy'
  >;
export const CascaderField = <T extends FieldValues>({
  name,
  label,
  control,
  options,
  errors,
  required,
  hidden,
  fieldNames,
  multiple,
  disabled,
  loading,
  enableIntermediate,
  displayRender,
  allowClear: _allowClear,
  placeholder,
  onChange,
  shouldCoexistOnChange = false,
  buttonLabel,
  showCheckedStrategy = 'SHOW_PARENT',
  wrapperStyle,
  width = '100%',
  hint,
  buttonOnClick,
  itemConnector = NEST_ITEM_CONNECTOR,
  useNotInOption = true,
  textNotInOption,
  labelSize = 'medium',
  shouldDisplayLabelWithTooltip = false,
  caption,
  showFullPath,
}: Props<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonWidth = buttonRef.current?.offsetWidth;
  const allowClear = _allowClear ?? !required;

  const cascaderStyle = {
    height: 'auto',
    ...(buttonWidth ? { width: `calc(100% - ${buttonWidth}px)` } : undefined),
  };

  const renderMultilineValue = (text: string[]) => {
    return (
      <span
        style={{
          whiteSpace: 'initial',
          display: 'inline-block',
          lineHeight: 1.5,
          padding: '2px 0',
          fontWeight: 'regular',
        }}
      >
        {loading ? null : text.join(itemConnector)}
      </span>
    );
  };

  const renderLabelWithCustomToolTip = (
    labels: string[],
    displayedLabel: string,
  ) => {
    return (
      <Tooltip title={labels.join(itemConnector)}>{displayedLabel}</Tooltip>
    );
  };

  // https://github.com/react-component/cascader/blob/4ade83bb928fd2b90334bef11ad9c6b3806b20d8/src/hooks/useDisplayValues.ts#L21
  const defaultDisplayRender = (labels: string[]) => {
    const mergedLabels: (string | number | ReactNode)[] = multiple
      ? labels.slice(-1)
      : labels;
    if (
      mergedLabels.every((label) => ['string', 'number'].includes(typeof label))
    ) {
      const displayedLabel = mergedLabels.join(itemConnector);
      return shouldDisplayLabelWithTooltip
        ? renderLabelWithCustomToolTip(labels, displayedLabel)
        : displayedLabel;
    }

    // If exist non-string value, use ReactNode instead
    return mergedLabels.reduce(
      (
        list: ReactNode[],
        label: string | number | ReactNode,
        index: number,
      ) => {
        const keyedLabel: string | number | ReactNode = isValidElement(label)
          ? // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            cloneElement(label, { key: index })
          : label;

        if (index === 0) {
          return [keyedLabel];
        }

        return list.concat([itemConnector, keyedLabel]);
      },
      [],
    );
  };

  // Recursively search for `options[].children` and return the largest number of it
  const findMaxDepthLevel = (options: CascaderOption[], currentLevel = 1) => {
    let maxLevel = currentLevel;
    for (const { children } of options) {
      if (children) {
        const childMaxLevel = findMaxDepthLevel(children, currentLevel + 1);
        maxLevel = Math.max(maxLevel, childMaxLevel);
      }
    }
    return maxLevel;
  };

  const [hasSearchValue, setHasSearchValue] = useState(false);

  const { width: windowWidth } = useWindowSize();

  const columnMaxWidth =
    !options?.length || hasSearchValue
      ? `${windowWidth}px`
      : `${windowWidth / findMaxDepthLevel(options)}px`;

  return (
    <Form.Item
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
          />
        )
      }
      hidden={hidden}
      style={wrapperStyle}
    >
      {caption && (
        <Box marginTop={-4} marginBottom={8}>
          {typeof caption === 'string' ? (
            <Text
              text={caption}
              color="secondary"
              weight="regular"
              style={{ fontSize: 12 }}
            />
          ) : (
            caption
          )}
        </Box>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const { onChange: fieldOnChange, ...restField } = field;

          return (
            <Flex style={{ width }} gap={4} className="CascaderFieldRenderRoot">
              <Cascader
                {...restField}
                style={cascaderStyle}
                options={options}
                multiple={multiple}
                fieldNames={fieldNames}
                placeholder={placeholder}
                disabled={disabled}
                loading={loading}
                allowClear={allowClear}
                changeOnSelect={enableIntermediate}
                showCheckedStrategy={showCheckedStrategy}
                popupClassName="CascaderFieldRenderRoot"
                displayRender={(
                  label: string[],
                  selectedOptions?: any[] | undefined,
                ) => {
                  if (loading) {
                    return '';
                  }
                  if (
                    useNotInOption &&
                    selectedOptions?.some((v) => v === null)
                  ) {
                    return textNotInOption ?? t('not-exist-in-option');
                  }
                  if (multiple) {
                    if (showFullPath) {
                      return label.map((v) => v).join(NEST_ITEM_CONNECTOR);
                    }
                    return defaultDisplayRender(label);
                  }
                  if (displayRender) {
                    return displayRender(label, selectedOptions);
                  }
                  return renderMultilineValue(label);
                }}
                dropdownRender={(menu) => {
                  return (
                    <Box
                      width="max-content"
                      marginLeft="auto"
                      marginRight="auto"
                    >
                      {menu}
                    </Box>
                  );
                }}
                dropdownMenuColumnStyle={{
                  maxWidth: columnMaxWidth,
                  textOverflow: 'initial',
                  whiteSpace: 'initial',
                }}
                onChange={(v) => {
                  const value = v as CascaderValueType | CascaderValueType[];
                  if (shouldCoexistOnChange || !onChange) {
                    // NOTE: If allowClear is true, value can be null but type of value is not nullable.
                    fieldOnChange(value || null);
                  }
                  onChange?.(
                    // NOTE: If allowClear is true, value can be null but type of value is not nullable.
                    value
                      ? (value as CascaderValueType & CascaderValueType[])
                      : null,
                  );
                  setHasSearchValue(false);
                }}
                onSearch={(value) => {
                  setHasSearchValue(!!value);
                }}
                showSearch={{
                  filter: (inputValue, options): boolean => {
                    return options.some(({ label }) => {
                      if (label === undefined || label === null) {
                        return false;
                      }
                      return String(label)
                        .toLowerCase()
                        .includes(inputValue.toLowerCase());
                    });
                  },
                  // falseの設定とfilterは両立できない
                  limit: 100,
                  matchInputWidth: false,
                }}
              />
              {buttonOnClick && buttonLabel && (
                <Button
                  ref={buttonRef}
                  disabled={disabled}
                  onClick={buttonOnClick}
                >
                  {buttonLabel}
                </Button>
              )}
            </Flex>
          );
        }}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
